import type { App, Plugin } from 'vue';

export const VERSION = '1.0.0';

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
export type LogType = 'console' | 'network' | 'error';
export type DownloadFormat = 'txt' | 'json' | 'csv';

export interface LogEntry {
    time: string;
    level: LogLevel;
    message: string;
    url: string;
    userAgent: string;
    environment: 'development' | 'production';
    type: LogType;
    status?: number;
    statusText?: string;
    duration?: string;
    requestBody?: string;
    responseBody?: string;
    method?: string;
    error?: string;
    stack?: string;
}

export interface SmartVConsoleOptions {
    showVConsole?: boolean;
    exposeGlobal?: boolean;
    maxLogs?: number;
}

export interface LoggerInstance {
    downloadLogs(format?: DownloadFormat): void;
    clearLogs(): void;
    getLogs(): LogEntry[];
    getLogCount(): number;
    addLog(level: LogLevel, args: unknown[], type?: LogType, extra?: Partial<LogEntry>): void;
}

export interface GlobalInterceptorRuntime {
    restore(): void;
    destroy(): void;
}

export interface LoggerWindow extends Window {
    __xhSmartVConsoleRuntime__?: GlobalInterceptorRuntime;
    $logger?: LoggerInstance;
    downloadLogs?: (format?: DownloadFormat) => void;
    clearLogs?: () => void;
    getLogs?: () => LogEntry[];
}

interface LoggerOptions {
    maxLogs: number;
    isProd: boolean;
}

// 管理日志数据、格式转换和文件下载，不负责修改浏览器运行时。
export class LoggerStore implements LoggerInstance {
    private logs: LogEntry[] = [];
    private lastDownloadTime = 0;
    private readonly maxLogs: number;
    private readonly isProd: boolean;

    constructor(options: LoggerOptions) {
        this.maxLogs = options.maxLogs;
        this.isProd = options.isProd;
    }

    addLog(
        level: LogLevel,
        args: unknown[],
        type: LogType = 'console',
        extra: Partial<LogEntry> = {}
    ): void {
        const logEntry: LogEntry = {
            time: new Date().toISOString(),
            level,
            message: this.formatArgs(args),
            url: window.location.href,
            userAgent: navigator.userAgent,
            environment: this.isProd ? 'production' : 'development',
            type,
            ...extra
        };

        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }
    }

    downloadLogs(format: DownloadFormat = 'txt'): void {
        const now = Date.now();
        if (now - this.lastDownloadTime < 2000) {
            console.warn('下载太频繁，请稍后再试');
            return;
        }
        this.lastDownloadTime = now;

        if (this.logs.length === 0) {
            console.warn('暂无日志');
            return;
        }

        const envPrefix = this.isProd ? 'prod' : 'dev';
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const output = this.createDownload(format);
        this.downloadFile(
            output.content,
            `log-${envPrefix}-${timestamp}.${format}`,
            output.mimeType
        );
    }

    clearLogs(): void {
        this.logs = [];
        console.log('日志已清空');
    }

    getLogs(): LogEntry[] {
        return [...this.logs];
    }

    getLogCount(): number {
        return this.logs.length;
    }

    private formatArgs(args: unknown[]): string {
        return args.map(arg => {
            if (typeof arg === 'object') {
                try {
                    return JSON.stringify(arg, null, 2);
                } catch (error) {
                    return String(arg);
                }
            }
            return String(arg);
        }).join(' ');
    }

    private createDownload(format: DownloadFormat): { content: string; mimeType: string } {
        switch (format) {
            case 'json':
                return { content: this.toJSON(), mimeType: 'application/json' };
            case 'csv':
                return { content: this.toCSV(), mimeType: 'text/csv' };
            default:
                return { content: this.toTxt(), mimeType: 'text/plain' };
        }
    }

    private toTxt(): string {
        const lines: string[] = [];
        lines.push('='.repeat(80));
        lines.push('应用程序日志');
        lines.push('='.repeat(80));
        lines.push(`生成时间: ${new Date().toLocaleString('zh-CN')}`);
        lines.push(`环境: ${this.isProd ? '生产环境' : '开发环境'}`);
        lines.push(`日志总数: ${this.logs.length}`);
        lines.push(`页面地址: ${window.location.href}`);
        lines.push(`用户代理: ${navigator.userAgent}`);
        lines.push('='.repeat(80));
        lines.push('');

        const consoleLogs = this.logs.filter(log => log.type === 'console');
        const networkLogs = this.logs.filter(log => log.type === 'network');
        const errorLogs = this.logs.filter(log => log.type === 'error');
        lines.push('📊 日志统计:');
        lines.push(`  - 控制台日志: ${consoleLogs.length} 条`);
        lines.push(`  - 网络请求: ${networkLogs.length} 条`);
        lines.push(`  - 错误信息: ${errorLogs.length} 条`);
        lines.push('');
        lines.push('='.repeat(80));
        lines.push('');

        this.logs.forEach((log, index) => {
            const time = new Date(log.time).toLocaleString('zh-CN');
            if (log.type === 'network') {
                lines.push(`【${index + 1}】网络请求 - ${time}`);
                lines.push(`  请求: ${log.message}`);
                if (log.status) lines.push(`  状态: ${log.status} ${log.statusText || ''}`);
                if (log.duration) lines.push(`  耗时: ${log.duration}`);
                if (log.requestBody) lines.push('  请求体:', `    ${log.requestBody}`);
                if (log.responseBody) lines.push('  响应体:', `    ${log.responseBody}`);
                if (log.error) lines.push(`  错误: ${log.error}`);
            } else if (log.type === 'error') {
                lines.push(`【${index + 1}】❌ 错误 - ${time}`);
                lines.push(`  信息: ${log.message}`);
                if (log.stack) lines.push('  堆栈:', `    ${log.stack}`);
            } else {
                lines.push(`【${index + 1}】${this.getLevelIcon(log.level)} ${log.level.toUpperCase()} - ${time}`);
                lines.push(`  ${log.message}`);
            }
            lines.push('-'.repeat(80));
        });
        return lines.join('\n');
    }

    private toJSON(): string {
        const formattedLogs = this.logs.map((log, index) => ({
            index: index + 1,
            time: log.time,
            timeFormatted: new Date(log.time).toLocaleString('zh-CN'),
            type: log.type,
            level: log.level.toUpperCase(),
            message: log.message,
            ...(log.type === 'network' && {
                method: log.method,
                status: log.status,
                statusText: log.statusText,
                duration: log.duration,
                requestBody: log.requestBody,
                responseBody: log.responseBody,
                error: log.error
            }),
            ...(log.type === 'error' && { stack: log.stack }),
            pageUrl: log.url,
            environment: log.environment
        }));

        return JSON.stringify({
            metadata: {
                generatedAt: new Date().toISOString(),
                environment: this.isProd ? 'production' : 'development',
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                totalLogs: this.logs.length,
                stats: {
                    console: this.logs.filter(log => log.type === 'console').length,
                    network: this.logs.filter(log => log.type === 'network').length,
                    errors: this.logs.filter(log => log.type === 'error').length
                }
            },
            logs: formattedLogs
        }, null, 2);
    }

    private toCSV(): string {
        const headers = ['序号', '时间', '类型', '级别', '消息', '方法', '状态', '耗时', '页面URL'];
        const rows = this.logs.map((log, index) => [
            index + 1,
            new Date(log.time).toLocaleString('zh-CN'),
            log.type,
            log.level.toUpperCase(),
            `"${log.message.replace(/"/g, '""')}"`,
            log.method || '',
            log.status || '',
            log.duration || '',
            log.url
        ]);
        return [headers, ...rows].map(row => row.join(',')).join('\n');
    }

    private getLevelIcon(level: LogLevel): string {
        const icons: Record<LogLevel, string> = {
            log: '📝', info: 'ℹ️', warn: '⚠️', error: '❌', debug: '🔍'
        };
        return icons[level];
    }

    private downloadFile(content: string, filename: string, mimeType: string): void {
        const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
    }
}

interface VConsoleInstance {
    destroy?(): void;
}

interface XhrLogContext {
    url: string;
    method: string;
    requestBody: string;
    startTime: number;
}

interface XhrListenerEntry {
    xhr: XMLHttpRequest;
    onLoad: () => void;
    onError: () => void;
    onLoadEnd: () => void;
}

const MAX_LOG_TEXT_LENGTH = 500;
const MAX_RESPONSE_CAPTURE_BYTES = 16 * 1024;
const SKIPPED_RESPONSE_TYPES = [
    'application/octet-stream', 'application/pdf', 'multipart/', 'text/event-stream',
    'image/', 'audio/', 'video/'
];

interface NetworkLogContext {
    url: string;
    method: string;
    requestBody?: string;
    duration: string;
}

// 管理 vConsole 面板与浏览器全局拦截器，销毁时完整恢复原运行时。
export class VConsoleRuntime {
    private vConsole: VConsoleInstance | null = null;
    private restoreInterceptors: (() => void) | null = null;
    private isDestroyed = false;

    constructor(private readonly logger: LoggerInstance, showVConsole: boolean) {
        if (showVConsole) {
            void this.createVConsole();
        }
        this.installGlobalInterceptors();
    }

    destroy(): void {
        this.isDestroyed = true;
        this.restoreInterceptors?.();
        this.restoreInterceptors = null;
        this.vConsole?.destroy?.();
        this.vConsole = null;
    }

    private async createVConsole(): Promise<void> {
        try {
            const { default: VConsole } = await import('vconsole');
            if (!this.isDestroyed) {
                this.vConsole = new VConsole();
            }
        } catch (error) {
            console.warn('Failed to create VConsole:', error);
        }
    }

    // 同一窗口只保留一个拦截实例，避免应用重建或 HMR 后重复采集。
    private installGlobalInterceptors(): void {
        const loggerWindow = window as LoggerWindow;
        loggerWindow.__xhSmartVConsoleRuntime__?.destroy();

        const restoreHandlers = [
            this.interceptConsole(),
            this.interceptFetch(),
            this.interceptXHR(),
            this.listenErrors()
        ];
        let isRestored = false;
        const restore = (): void => {
            if (isRestored) return;
            isRestored = true;
            restoreHandlers.reverse().forEach(restoreHandler => restoreHandler());
            if (loggerWindow.__xhSmartVConsoleRuntime__?.restore === restore) {
                delete loggerWindow.__xhSmartVConsoleRuntime__;
            }
        };

        loggerWindow.__xhSmartVConsoleRuntime__ = { restore, destroy: () => this.destroy() };
        this.restoreInterceptors = restore;
    }

    private addLog(
        level: LogLevel,
        args: unknown[],
        type: LogType = 'console',
        extra: Partial<LogEntry> = {}
    ): void {
        if (!this.isDestroyed) {
            this.logger.addLog(level, args, type, extra);
        }
    }

    private interceptConsole(): () => void {
        const methods: LogLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
        const originals = new Map<LogLevel, typeof console.log>();
        const wrappers = new Map<LogLevel, typeof console.log>();

        methods.forEach(method => {
            const original = console[method];
            const wrapper = (...args: unknown[]): void => {
                this.addLog(method, args);
                original.apply(console, args);
            };
            originals.set(method, original);
            wrappers.set(method, wrapper);
            console[method] = wrapper;
        });

        return () => methods.forEach(method => {
            if (console[method] === wrappers.get(method)) {
                console[method] = originals.get(method) as typeof console.log;
            }
        });
    }

    private interceptFetch(): () => void {
        if (typeof window.fetch !== 'function') return () => undefined;
        const originalFetch = window.fetch;
        const patchedFetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
            const startTime = Date.now();
            const request = args[0];
            const url = typeof request === 'string'
                ? request
                : request instanceof URL ? request.toString() : request.url;
            const method = (args[1]?.method || (request instanceof Request ? request.method : 'GET')).toUpperCase();
            const requestBody = args[1]?.body;

            try {
                const response = await originalFetch.apply(window, args);
                const context: NetworkLogContext = {
                    url,
                    method,
                    duration: `${Date.now() - startTime}ms`,
                    requestBody: requestBody ? String(requestBody).substring(0, MAX_LOG_TEXT_LENGTH) : undefined
                };
                // 克隆响应后异步读取，业务请求无需等待日志解析。
                void this.captureFetchResponse(response, context);
                return response;
            } catch (error: unknown) {
                this.addLog('error', [`${method} ${url} - FAILED`], 'network', {
                    duration: `${Date.now() - startTime}ms`,
                    error: this.getErrorMessage(error),
                    method
                });
                throw error;
            }
        };

        window.fetch = patchedFetch;
        return () => {
            if (window.fetch === patchedFetch) window.fetch = originalFetch;
        };
    }

    private async captureFetchResponse(response: Response, context: NetworkLogContext): Promise<void> {
        let responseBody: string | undefined;
        try {
            responseBody = await this.readLimitedResponseBody(response);
        } catch (error) {
            responseBody = '无法读取响应体';
        }
        this.addLog(response.ok ? 'info' : 'error', [`${context.method} ${context.url}`], 'network', {
            status: response.status,
            statusText: response.statusText,
            duration: context.duration,
            requestBody: context.requestBody,
            responseBody,
            method: context.method
        });
    }

    private async readLimitedResponseBody(response: Response): Promise<string | undefined> {
        if (!this.shouldCaptureResponse(response)) return undefined;
        const reader = response.clone().body?.getReader();
        if (!reader) return undefined;

        const chunks: Uint8Array[] = [];
        let receivedBytes = 0;
        let isReading = true;
        try {
            while (isReading) {
                const { done, value } = await reader.read();
                if (done) {
                    isReading = false;
                } else if (value) {
                    receivedBytes += value.byteLength;
                    if (receivedBytes > MAX_RESPONSE_CAPTURE_BYTES) {
                        await reader.cancel();
                        return undefined;
                    }
                    chunks.push(value);
                }
            }
        } finally {
            reader.releaseLock();
        }

        const body = new Uint8Array(receivedBytes);
        let offset = 0;
        chunks.forEach(chunk => {
            body.set(chunk, offset);
            offset += chunk.byteLength;
        });
        const text = new TextDecoder().decode(body);
        return text.length > MAX_LOG_TEXT_LENGTH ? `${text.substring(0, MAX_LOG_TEXT_LENGTH)}...` : text;
    }

    private shouldCaptureResponse(response: Response): boolean {
        const contentType = response.headers.get('content-type')?.toLowerCase() || '';
        const disposition = response.headers.get('content-disposition')?.toLowerCase() || '';
        const contentLength = Number(response.headers.get('content-length'));
        if (disposition.includes('attachment') || SKIPPED_RESPONSE_TYPES.some(type => contentType.includes(type))) {
            return false;
        }
        return !Number.isFinite(contentLength) || contentLength <= MAX_RESPONSE_CAPTURE_BYTES;
    }

    private interceptXHR(): () => void {
        if (typeof window.XMLHttpRequest === 'undefined') return () => undefined;
        const prototype = window.XMLHttpRequest.prototype;
        const originalOpen = prototype.open;
        const originalSend = prototype.send;
        const callOriginalOpen = originalOpen as unknown as (
            this: XMLHttpRequest,
            method: string,
            url: string | URL,
            async: boolean,
            username?: string | null,
            password?: string | null
        ) => void;
        const contexts = new WeakMap<XMLHttpRequest, XhrLogContext>();
        const listeners = new Set<XhrListenerEntry>();
        const addLog = this.addLog.bind(this);
        const getResponseBody = this.getXhrResponseBody.bind(this);

        const patchedOpen = function (
            this: XMLHttpRequest,
            method: string,
            url: string | URL,
            async?: boolean,
            username?: string | null,
            password?: string | null
        ): void {
            contexts.set(this, { url: url.toString(), method: method.toUpperCase(), requestBody: '', startTime: Date.now() });
            callOriginalOpen.call(this, method, url, async ?? true, username, password);
        } as typeof prototype.open;

        const patchedSend = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null): void {
            const context = contexts.get(this) || { url: '', method: 'GET', requestBody: '', startTime: Date.now() };
            context.requestBody = body ? String(body).substring(0, MAX_LOG_TEXT_LENGTH) : '';
            contexts.set(this, context);

            const cleanup = (): void => {
                this.removeEventListener('load', onLoad);
                this.removeEventListener('error', onError);
                this.removeEventListener('loadend', onLoadEnd);
                listeners.delete(listenerEntry);
            };
            const onLoad = (): void => {
                cleanup();
                addLog(this.status >= 200 && this.status < 400 ? 'info' : 'error', [`${context.method} ${context.url}`], 'network', {
                    status: this.status,
                    statusText: this.statusText,
                    duration: `${Date.now() - context.startTime}ms`,
                    requestBody: context.requestBody,
                    responseBody: getResponseBody(this),
                    method: context.method
                });
            };
            const onError = (): void => {
                cleanup();
                addLog('error', [`${context.method} ${context.url} - FAILED`], 'network', {
                    duration: `${Date.now() - context.startTime}ms`, error: 'Network error', method: context.method
                });
            };
            const onLoadEnd = (): void => cleanup();
            const listenerEntry: XhrListenerEntry = { xhr: this, onLoad, onError, onLoadEnd };
            listeners.add(listenerEntry);
            this.addEventListener('load', onLoad);
            this.addEventListener('error', onError);
            this.addEventListener('loadend', onLoadEnd);
            try {
                originalSend.call(this, body);
            } catch (error) {
                cleanup();
                throw error;
            }
        } as typeof prototype.send;

        prototype.open = patchedOpen;
        prototype.send = patchedSend;
        return () => {
            if (prototype.open === patchedOpen) prototype.open = originalOpen;
            if (prototype.send === patchedSend) prototype.send = originalSend;
            listeners.forEach(({ xhr, onLoad, onError, onLoadEnd }) => {
                xhr.removeEventListener('load', onLoad);
                xhr.removeEventListener('error', onError);
                xhr.removeEventListener('loadend', onLoadEnd);
            });
            listeners.clear();
        };
    }

    private getXhrResponseBody(xhr: XMLHttpRequest): string | undefined {
        try {
            if (xhr.responseType === '' || xhr.responseType === 'text') {
                return xhr.responseText?.substring(0, MAX_LOG_TEXT_LENGTH);
            }
            if (xhr.responseType === 'json') {
                return JSON.stringify(xhr.response)?.substring(0, MAX_LOG_TEXT_LENGTH);
            }
        } catch (error) {
            return '无法读取响应体';
        }
        return undefined;
    }

    private listenErrors(): () => void {
        const onError = (event: ErrorEvent): void => {
            this.addLog('error', [`${event.message} at ${event.filename}:${event.lineno}:${event.colno}`], 'error', {
                stack: event.error?.stack
            });
        };
        const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
            const reason = event.reason;
            this.addLog('error', ['Unhandled Promise Rejection:', reason?.message || String(reason)], 'error', {
                stack: reason?.stack
            });
        };
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
        };
    }

    private getErrorMessage(error: unknown): string {
        return error instanceof Error ? error.message : String(error);
    }
}


// 组合日志数据与浏览器运行时能力，对外维持原有 LoggerInstance API。
export class SmartVConsole implements LoggerInstance {
    private readonly logger: LoggerStore;
    private readonly runtime: VConsoleRuntime;
    private isDestroyed = false;

    constructor(options: SmartVConsoleOptions = {}) {
        const config: Required<SmartVConsoleOptions> = {
            showVConsole: false,
            exposeGlobal: true,
            maxLogs: 1000,
            ...options
        };
        this.logger = new LoggerStore({
            maxLogs: config.maxLogs,
            isProd: process.env.VUE_APP_ENV === 'production'
        });
        this.runtime = new VConsoleRuntime(this.logger, config.showVConsole);
    }

    addLog(level: LogLevel, args: unknown[], type: LogType = 'console', extra: Partial<LogEntry> = {}): void {
        if (!this.isDestroyed) {
            this.logger.addLog(level, args, type, extra);
        }
    }

    downloadLogs(format: DownloadFormat = 'txt'): void {
        this.logger.downloadLogs(format);
    }

    clearLogs(): void {
        this.logger.clearLogs();
    }

    getLogs(): LogEntry[] {
        return this.logger.getLogs();
    }

    getLogCount(): number {
        return this.logger.getLogCount();
    }

    destroy(): void {
        if (this.isDestroyed) return;
        this.isDestroyed = true;
        this.runtime.destroy();
    }
}

const SmartVConsolePlugin: Plugin<SmartVConsoleOptions> = {
    install(app: App, options: SmartVConsoleOptions = {}): void {
        const smartVConsole = new SmartVConsole(options);
        const loggerWindow = window as LoggerWindow;

        app.config.globalProperties.$logger = smartVConsole;
        app.provide('logger', smartVConsole);
        loggerWindow.$logger = smartVConsole;

        if (options.exposeGlobal !== false) {
            loggerWindow.downloadLogs = (format?: DownloadFormat) => smartVConsole.downloadLogs(format);
            loggerWindow.clearLogs = () => smartVConsole.clearLogs();
            loggerWindow.getLogs = () => smartVConsole.getLogs();
        }

        app.onUnmount(() => {
            smartVConsole.destroy();
            if (loggerWindow.$logger === smartVConsole) {
                delete loggerWindow.$logger;
            }
        });
    }
};

export default SmartVConsolePlugin;

